// Global
import React, { MouseEventHandler, Ref, useState } from 'react';
import { tv } from 'tailwind-variants';
import { Link, LinkField } from '@sitecore-jss/sitecore-jss-nextjs';

// Lib
import { hostToReplace } from 'lib/host-to-replace';

// Local
import SVG from 'helpers/SVG/SVG';
import classNames from 'classnames';
import useExperienceEditor from 'lib/use-experience-editor';

export const BUTTON_TYPES = ['default', 'secondary', 'auxiliary', 'action', undefined];
export const BUTTON_SIZES = ['large', 'medium', 'small', undefined];

interface Props {
  auto?: boolean;
  disabled?: boolean;
  iconLeft?: string;
  iconRight?: string;
  id?: string;
  label?: string;
  loading?: boolean;
  onClick?: MouseEventHandler;
  ref?: Ref<HTMLButtonElement | null>;
  tag?: string;
  title?: string;
  type?: string;
  size?: string;
  field?: LinkField;
  href?: string;
  target?: string;
  fieldClass?: string;
}

type NativeAttrs = Omit<React.ButtonHTMLAttributes<undefined>, keyof Props>;

type Variants = 'default' | 'secondary' | 'auxiliary' | 'action' | undefined;
type Sizes = 'full' | undefined;

export type ButtonProps = Props & NativeAttrs;

const button = tv({
  base: ['btn'],
  variants: {
    type: {
      default: ['btn-primary'],
      secondary: ['btn-secondary'],
      auxiliary: ['btn-auxiliary'],
      action: ['btn-action'],
    },
    disabled: {
      true: ['cursor-not-allowed'],
    },
    hasActionIconRight: {
      true: [
        'after:content-[""]',
        'after:w-6',
        'after:h-6',
        "after:bg-[url('/icon-action-chevron-right.svg')]",
        'after:bg-no-repeat',
        'after:bg-center',
      ],
    },
    size: {
      full: 'min-w-full',
    },
  },
});

const Button = ({
  disabled = false,
  iconLeft,
  iconRight,
  id,
  label,
  loading = false,
  onClick = (): void => undefined,
  ref,
  tag = 'button',
  title,
  type = 'default',
  size = '',
  field,
  href,
  target,
  fieldClass,
}: ButtonProps): JSX.Element => {
  const [showIcons /*, setShowIcons */] = useState<boolean | undefined>(true);
  const asLinkField = !field?.value ? { value: { ...field } } : (field as LinkField);

  const className = classNames(
    fieldClass,
    asLinkField.value.class,
    button({
      disabled: disabled,
      type: type as Variants,
      size: size as Sizes,
      hasActionIconRight: type === 'action' && iconRight === 'icon-chevron-right',
    })
  );

  const children = (
    <>
      {showIcons && iconLeft && <SVG svg={iconLeft} />}
      {showIcons && loading && <SVG svg={'spinner'} />}
      {label && !loading && label}
      {showIcons && iconRight && <SVG svg={iconRight} />}
    </>
  );

  const isEE = useExperienceEditor();

  const processedField = asLinkField ? hostToReplace(asLinkField) : undefined;

  return (
    <>
      {field?.value.href === undefined &&
        React.createElement(
          tag,
          {
            className,
            disabled,
            id,
            onClick: (evt: React.MouseEvent<Element, MouseEvent>) => onClick(evt),
            ref,
            title,
            href,
            target,
          },
          children
        )}
      {field?.value?.href || isEE ? (
        <Link
          field={processedField as LinkField}
          href={processedField?.value?.href}
          className={className}
          type={type}
          title={title}
          aria-label={field?.value?.title ? field?.value?.title : field?.value?.text}
          aria-describedby={field?.value?.title ? field?.value?.title : field?.value?.text}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : undefined}
        ></Link>
      ) : null}
    </>
  );
};

export default Button;
